import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, Paper } from '@mui/material';
import ShieldIcon from '@mui/icons-material/Shield';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useAuthStore } from '../store/authStore';
import { useThemeContext } from './contexts/ThemeContext';

export default function TwoFactorBanner() {
    const navigate = useNavigate();
    const location = useLocation();
    const { mode } = useThemeContext();
    const darkTheme = mode === 'dark';

    const { isAuthenticated, twoFactorEnabled, mfaSetupEnforced, daysRemaining, mfaDeadline } = useAuthStore();

    // Nie wyświetlaj banera jeśli użytkownik nie jest zalogowany, ma włączone 2FA lub jest w trybie przymusowym (gdzie jest modal)
    if (!isAuthenticated || twoFactorEnabled || mfaSetupEnforced) {
        return null;
    }

    // Nie wyświetlaj banera jeśli użytkownik jest już na stronie Ustawień w zakładce Bezpieczeństwo
    if (location.pathname === '/Settings' && location.search.includes('tab=security')) {
        return null;
    }

    const formattedDeadline = mfaDeadline
        ? new Date(mfaDeadline).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : null;

    const isUrgent = daysRemaining <= 7;

    return (
        <Paper
            elevation={0}
            sx={{
                mx: { xs: 1.5, sm: 3 },
                mt: 2,
                mb: 1.5,
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: isUrgent
                    ? (darkTheme ? 'rgba(239, 68, 68, 0.4)' : '#FCA5A5')
                    : (darkTheme ? 'rgba(245, 158, 11, 0.35)' : '#FDE68A'),
                background: isUrgent
                    ? (darkTheme ? 'linear-gradient(90deg, rgba(239, 68, 68, 0.12) 0%, rgba(220, 38, 38, 0.05) 100%)' : 'linear-gradient(90deg, #FEF2F2 0%, #FFF5F5 100%)')
                    : (darkTheme ? 'linear-gradient(90deg, rgba(245, 158, 11, 0.12) 0%, rgba(217, 119, 6, 0.05) 100%)' : 'linear-gradient(90deg, #FFFBEB 0%, #FEF9C3 100%)'),
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: 'space-between',
                gap: 2,
                boxShadow: darkTheme ? '0 2px 8px rgba(0,0,0,0.2)' : '0 1px 4px rgba(0,0,0,0.04)'
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.8 }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: isUrgent
                            ? 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'
                            : 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                        color: '#FFFFFF',
                        flexShrink: 0,
                        boxShadow: isUrgent ? '0 2px 8px rgba(239, 68, 68, 0.3)' : '0 2px 8px rgba(245, 158, 11, 0.3)'
                    }}
                >
                    <ShieldIcon fontSize="small" />
                </Box>
                <Box>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 700,
                            color: darkTheme ? '#F8FAFC' : '#0F172A',
                            fontSize: '13.5px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        Włącz weryfikację dwuetapową (2FA)
                        <Typography
                            component="span"
                            sx={{
                                fontSize: '12px',
                                fontWeight: 700,
                                px: 1,
                                py: 0.2,
                                borderRadius: '6px',
                                backgroundColor: isUrgent
                                    ? (darkTheme ? 'rgba(239, 68, 68, 0.25)' : '#FEE2E2')
                                    : (darkTheme ? 'rgba(245, 158, 11, 0.25)' : '#FEF3C7'),
                                color: isUrgent ? '#EF4444' : (darkTheme ? '#FBBF24' : '#B45309')
                            }}
                        >
                            Pozostało: {daysRemaining} {daysRemaining === 1 ? 'dzień' : 'dni'}
                        </Typography>
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: darkTheme ? '#CBD5E1' : '#475569',
                            fontSize: '12.5px',
                            mt: 0.2
                        }}
                    >
                        Zgodnie z polityką bezpieczeństwa wymagane jest zabezpieczenie konta aplikacją Google Authenticator
                        {formattedDeadline ? ` do dnia ${formattedDeadline}` : ''}. Po tym terminie logowanie zostanie zablokowane.
                    </Typography>
                </Box>
            </Box>

            <Button
                variant="contained"
                size="small"
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/Settings?tab=security')}
                sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '13px',
                    px: 2.2,
                    py: 0.8,
                    flexShrink: 0,
                    alignSelf: { xs: 'stretch', md: 'center' },
                    background: isUrgent
                        ? 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'
                        : 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    '&:hover': {
                        background: isUrgent
                            ? 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)'
                            : 'linear-gradient(135deg, #059669 0%, #047857 100%)'
                    }
                }}
            >
                Przejdź do ustawień 2FA
            </Button>
        </Paper>
    );
}

